import { COLORS } from '@btime/constants'
import { is, isEmpty } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { Grid } from '../../structure/Grid'
import { HorizontalLine } from '../../../../../components/shapes/Line'
import Line from '../../helpers/Line'
import Modal from '../../../../../components/structure/Modal'
import { DatePickerFooter } from '../../../../../components/form/modalPicker/ModalPickerFooter'
import ModalPickerHeader from '../../../../../components/form/modalPicker/ModalPickerHeader'
import Separator from '../../helpers/Separator'
import SmallCalendar from './SmallCalendar'
import Tag from '../../presentation/Tag'
import Text from '../../presentation/Text'
import { genRelativeDateValues } from '../../../../../utils/helpers/date'

const formatToMoment = value => {
  if (!value || !value.length) return value
  const [init, end] = value
  if (!is(String, init)) return value
  return [moment(init).toDate(), moment(end).toDate()]
}

const getRecommendedOptions = t => [
  { label: t('common:datePicker.today'), value: { type: 'day' } },
  { label: t('common:datePicker.yesterday'), value: { type: 'day', init: 1, end: 1 } },
  { label: t('common:datePicker.currentWeek'), value: { type: 'week' } },
  { label: t('common:datePicker.lastWeek'), value: { type: 'week', init: 1, end: 1 } },
  { label: t('common:datePicker.currentMonth'), value: { type: 'month' } },
  { label: t('common:datePicker.lastMonth'), value: { type: 'month', init: 1, end: 1 } },
  { label: t('common:datePicker.currentQuarter'), value: { type: 'quarter' } },
  { label: t('common:datePicker.lastQuarter'), value: { type: 'quarter', init: 1, end: 1 } },
  { label: t('common:datePicker.currentYear'), value: { type: 'year' } },
  { label: t('common:datePicker.lastYear'), value: { type: 'year', init: 1, end: 1 } },
  { label: t('common:datePicker.last3Years'), value: { type: 'year', init: 3, end: 0 } },
  { label: t('common:datePicker.last5Years'), value: { type: 'year', init: 5, end: 0 } },
  { label: t('common:datePicker.next7Days'), value: { type: 'day', init: 0, end: 7, mode: 'add' } }
]

const computeInitialValue = value => {
  const formattedValue = value ? genRelativeDateValues(value) : value
  return formatToMoment(formattedValue)
}

const DatePickerModal = ({ open, value, onChange, onClose, level, recommendedOptions, ...props }) => {
  const { t } = useTranslation('common')
  const [localValue, setLocalValue] = React.useState(() => computeInitialValue(value))

  React.useEffect(() => {
    setLocalValue(computeInitialValue(value))
  }, [value])

  const handleSubmit = value => {
    const valueToSubmit = !value && (!localValue || isEmpty(localValue)) ? undefined : value || localValue
    onChange(valueToSubmit)
    onClose()
  }

  const handleClear = () => {
    setLocalValue(undefined)
    onChange(undefined)
    onClose()
  }

  const optionsToUse = recommendedOptions || getRecommendedOptions(t)

  return (
    <Modal width="24rem" noPadding open={open} onClose={() => onClose()} level={level}>
      <ModalPickerHeader title={t('datePicker.selectDate')} disableControl {...props} />

      <div>
        <HorizontalLine thin lineColor={COLORS.light3} />
        <Separator weight={4} />
        <SmallCalendar
          value={localValue ? genRelativeDateValues(localValue) : undefined}
          onChange={setLocalValue}
          {...props}
        />
      </div>

      {props.useRange && (
        <Separator paddingH={1.25}>
          <Text size="small" color="dark3">
            {t('datePicker.suggestions')}:
          </Text>
          <Separator weight={1} />
          <Grid wrap gap={0.4}>
            {optionsToUse.map(option => (
              <Tag
                key={option.label}
                transparent
                size="small"
                color="dark5"
                onClick={() => {
                  const normalized = genRelativeDateValues(option.value)
                  setLocalValue(normalized)
                  handleSubmit(normalized)
                }}
              >
                {option.label}
              </Tag>
            ))}
          </Grid>
          <Separator weight={4} />
        </Separator>
      )}

      <Line />

      <DatePickerFooter closeModal={() => handleSubmit()} onChange={() => { }} handleClear={handleClear} />
    </Modal>
  )
}

export default DatePickerModal
